/**
 * 商品API请求封装
 */
class ProductAPI {
  /**
   * 获取商品详情
   * @param {string} handle - 商品handle
   * @returns {Promise} 商品详情数据
   */
  static async getProductDetails(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error("获取商品详情失败");
      return await response.json();
    } catch (error) {
      console.error("获取商品详情出错:", error);
      throw error;
    }
  }

  /**
   * 增加购物车商品数量
   * @param {number} variantId - 商品变体ID
   * @param {number} quantity - 要增加的数量
   * @returns {Promise} 更新后的购物车数据
   */
  static async addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: quantity,
            },
          ],
        }),
      });

      if (!response.ok) throw new Error("添加商品失败");
      return await response.json();
    } catch (error) {
      console.error("添加商品出错:", error);
      throw error;
    }
  }

  /**
   * 更新购物车商品数量
   * @param {number} variantId - 商品变体ID
   * @param {number} quantity - 新的数量
   * @returns {Promise} 更新后的购物车数据
   */
  static async updateCartQuantity(variantId, quantity) {
    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          updates: {
            [variantId]: quantity,
          },
        }),
      });

      if (!response.ok) throw new Error("更新数量失败");
      return await response.json();
    } catch (error) {
      console.error("更新数量出错:", error);
      throw error;
    }
  }

  /**
   * 减少购物车商品数量
   * @param {number} variantId - 商品变体ID
   * @param {number} quantity - 要减少的数量
   * @returns {Promise} 更新后的购物车数据
   */
  static async decreaseCartQuantity(variantId, quantity = 1) {
    try {
      // 先获取当前购物车数据
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();

      // 找到对应商品的当前数量
      const item = cart.items.find((item) => item.variant_id === variantId);
      if (!item) throw new Error("商品不存在");

      // 计算新数量
      const newQuantity = Math.max(0, item.quantity - quantity);

      // 更新购物车
      return await this.updateCartQuantity(variantId, newQuantity);
    } catch (error) {
      console.error("减少数量出错:", error);
      throw error;
    }
  }

  /**
   * 获取购物车信息
   * @returns {Promise} 购物车数据
   */
  static async getCart() {
    try {
      const response = await fetch("/cart.js");
      if (!response.ok) throw new Error("获取购物车失败");
      return await response.json();
    } catch (error) {
      console.error("获取购物车出错:", error);
      throw error;
    }
  }

  /**
   * 应用折扣码
   * @param {string} discountCode - 折扣码
   * @returns {Promise} 更新后的购物车数据
   */
  static async applyDiscountCode(discountCode) {
    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discount: discountCode,
        }),
      });

      if (!response.ok) throw new Error("应用折扣码失败");
      return await response.json();
    } catch (error) {
      console.error("应用折扣码出错:", error);
      throw error;
    }
  }

  /**
   * 移除折扣码
   * @returns {Promise} 更新后的购物车数据
   */
  static async removeDiscountCode() {
    try {
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discount: "",
        }),
      });

      if (!response.ok) throw new Error("移除折扣码失败");
      return await response.json();
    } catch (error) {
      console.error("移除折扣码出错:", error);
      throw error;
    }
  }

  /**
   * 获取单个商品变体详情
   * @param {number} variantId - 商品变体ID
   * @returns {Promise} 商品变体详情数据
   */
  static async getVariantById(variantId) {
    try {
      const response = await fetch(`/variants/${variantId}.js`);
      if (!response.ok) throw new Error("获取变体详情失败");
      return await response.json();
    } catch (error) {
      console.error("获取变体详情出错:", error);
      throw error;
    }
  }

  /**
   * 生成图片sizes属性
   * @private
   */
  static generateSizes(settings) {
    const pageWidth = settings?.page_width || 1200;
    const mediaWidth = settings?.media_width || 0.65;

    return `
      (min-width: ${pageWidth}px) calc((${pageWidth * mediaWidth} - 4rem) / 4),
      (min-width: 990px) calc((${mediaWidth * 100}vw - 4rem) / 4),
      (min-width: 750px) calc((100vw - 15rem) / 8),
      calc((100vw - 8rem) / 3)
    `.trim();
  }
}
window.onpopstate = function (event) {
  console.log(event);
};
/**
 *
 * @param {*} param0
 * @param {*} historyChange
 */
window.pushState = ({ url }, historyChange) => {
  window.history.pushState({}, "", url);
  historyChange && historyChange();
};
// 使用示例:
/*
// 获取商品详情
ProductAPI.getProductDetails('product-handle')
  .then(product => {
    console.log('商品详情:', product);
  })
  .catch(error => {
    console.error(error);
  });

// 增加商品
ProductAPI.addToCart(variantId, 1)
  .then(cart => {
    console.log('添加成功:', cart);
  })
  .catch(error => {
    console.error(error);
  });

// 减少商品
ProductAPI.decreaseCartQuantity(variantId, 1)
  .then(cart => {
    console.log('减少成功:', cart);
  })
  .catch(error => {
    console.error(error);
  });

// 获取单个变体详情
ProductAPI.getVariantById(variantId)
  .then(variant => {
    console.log('变体详情:', variant);
  })
  .catch(error => {
    console.error(error);
  });

// 重新渲染 media-gallery
ProductAPI.reloadMediaGallery({
  galleryHtml: '<media-gallery>新的内容</media-gallery>',
  position: 'left',
  layout: 'stacked',
  size: 'large'
});
*/

window.ProductAPI = ProductAPI;
